/**
 * Client Weekly Check-in Page (Epic 5)
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ClipboardCheck, Loader2, Battery, Moon, Brain,
} from "lucide-react";
import { useMyCheckins, submitCheckin } from "../hooks/useNutrition";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Slider } from "../components/ui/slider";
import { Toaster, toast } from "sonner";

const containerV = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.08 } } };
const itemV = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };

const SLIDER_CONFIG = [
  { key: "energy_level", label: "Niveau d'énergie", icon: Battery, color: "text-lime-400", bgLow: "bg-red-500", bgHigh: "bg-lime-400" },
  { key: "sleep_quality", label: "Qualité du sommeil", icon: Moon, color: "text-violet-400", bgLow: "bg-red-500", bgHigh: "bg-violet-400" },
  { key: "stress_level", label: "Niveau de stress", icon: Brain, color: "text-rose-400", bgLow: "bg-lime-400", bgHigh: "bg-rose-400" },
];

function getSliderColor(key, value) {
  if (key === "stress_level") return value <= 4 ? "bg-lime-400" : value <= 7 ? "bg-amber-400" : "bg-rose-400";
  return value >= 7 ? "bg-lime-400" : value >= 4 ? "bg-amber-400" : "bg-rose-400";
}

export default function CheckinPage() {
  const navigate = useNavigate();
  const { checkins, mutate } = useMyCheckins();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    checkin_date: new Date().toISOString().split("T")[0],
    energy_level: 5,
    sleep_quality: 5,
    stress_level: 5,
    remarks: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await submitCheckin({
        ...form,
        remarks: form.remarks || null,
      });
      toast.success("Bilan envoyé");
      setForm({ checkin_date: new Date().toISOString().split("T")[0], energy_level: 5, sleep_quality: 5, stress_level: 5, remarks: "" });
      mutate();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 pb-20">
      <Toaster position="top-right" richColors />
      <header className="sticky top-0 z-40 glass border-b border-zinc-800">
        <div className="max-w-lg mx-auto px-4 flex items-center justify-between h-14">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard" className="text-zinc-400 hover:text-white -ml-2">
            <ArrowLeft className="w-5 h-5 mr-1" /> Retour
          </Button>
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-violet-400" />
            <span className="font-heading font-bold text-white">BILAN HEBDO</span>
          </div>
          <div className="w-16" />
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        <motion.div variants={containerV} initial="hidden" animate="show" className="space-y-6">
          {/* Check-in Form */}
          <motion.div variants={itemV}>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-white">Nouveau bilan</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Date</Label>
                    <Input type="date" value={form.checkin_date} onChange={(e) => setForm({ ...form, checkin_date: e.target.value })} required data-testid="checkin-date" className="bg-zinc-950 border-zinc-800 h-11" />
                  </div>

                  {SLIDER_CONFIG.map(({ key, label, icon: Icon, color }) => (
                    <div key={key} className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-zinc-300 flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${color}`} /> {label}
                        </Label>
                        <span className={`text-lg font-bold ${getSliderColor(key, form[key]).replace("bg-", "text-")}`}>
                          {form[key]}/10
                        </span>
                      </div>
                      <div className="relative">
                        <Slider
                          min={1} max={10} step={1}
                          value={[form[key]]}
                          onValueChange={([v]) => setForm({ ...form, [key]: v })}
                          data-testid={`checkin-${key}`}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-zinc-600 mt-1">
                          <span>1</span>
                          <span>5</span>
                          <span>10</span>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="space-y-2">
                    <Label className="text-zinc-300">Remarques</Label>
                    <textarea
                      value={form.remarks}
                      onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                      placeholder="Comment s'est passée votre semaine ?"
                      data-testid="checkin-remarks"
                      rows={3}
                      className="w-full bg-zinc-950 border border-zinc-800 rounded-lg p-3 text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-400/50"
                    />
                  </div>

                  <Button type="submit" disabled={saving} data-testid="submit-checkin" className="w-full h-12 bg-violet-500 hover:bg-violet-600 text-white font-bold uppercase tracking-wider rounded-full">
                    {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Envoyer le bilan"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* History */}
          <motion.div variants={itemV}>
            <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-wider mb-3">Historique</h3>
            {checkins.length > 0 ? (
              <div className="space-y-3">
                {checkins.map((c) => (
                  <Card key={c.id} className="bg-zinc-900/50 border-zinc-800" data-testid={`checkin-${c.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Badge className="bg-violet-400/10 text-violet-400 border-violet-400/30 text-xs">
                          {new Date(c.checkin_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-center">
                        {SLIDER_CONFIG.map(({ key, label, icon: Icon, color }) => (
                          <div key={key} className="p-2 bg-zinc-950/50 rounded-lg">
                            <Icon className={`w-4 h-4 mx-auto mb-1 ${color}`} />
                            <p className="text-lg font-bold text-white">{c[key]}</p>
                            <p className="text-xs text-zinc-500">{label.split(" ").pop()}</p>
                          </div>
                        ))}
                      </div>
                      {c.remarks && <p className="text-sm text-zinc-400 mt-3 italic">"{c.remarks}"</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="py-10 text-center">
                  <ClipboardCheck className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
                  <p className="text-zinc-500">Aucun bilan soumis</p>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </motion.div>
      </main>
    </div>
  );
}
